import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaComments } from 'react-icons/fa';
import api from '../utils/api';
import './PublicProfile.css';

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('about');
  const [isOwner, setIsOwner] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    fetchPublicProfile();
  }, [userId]);

  const fetchPublicProfile = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      console.log('Fetching public profile for userId:', userId);
      
      const response = await api.get(`/public-profile/${userId}`);
      
      console.log('Response:', response.data);
      
      if (response.data.success) {
        setProfile(response.data.profile);
        setIsOwner(response.data.isOwner);
        setIsLoggedIn(response.data.isLoggedIn);
      } else {
        setError(response.data.message || 'Failed to load profile');
      }
    } catch (err) {
      console.error('Error fetching public profile:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setError(err.response?.data?.message || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = () => {
    const link = window.location.href;
    navigator.clipboard.writeText(link);
    alert('Profile link copied to clipboard!');
  };

  const shareOnWhatsApp = () => {
    const link = window.location.href;
    const text = `Check out ${profile.businessName || 'this profile'} on Cixio`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + link)}`, '_blank');
  };

  const handleStartChat = async () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      // Save current URL to return after login
      const currentUrl = window.location.pathname;
      toast.info('Please login to send a message');
      navigate(`/login?returnUrl=${encodeURIComponent(currentUrl)}`);
      return;
    }

    try {
      const profileName = profile.businessName || 
        `${profile.consumerProfile?.firstName || ''} ${profile.consumerProfile?.lastName || ''}`.trim() ||
        'User';
      
      const response = await api.post('/chat/start', {
        recipientId: userId,
        message: `Hi ${profileName}, I'd like to connect with you.`
      });
      
      if (response.data.success) {
        toast.success('Chat started successfully!');
        navigate(`/chat/${response.data.chat._id}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error(error.response?.data?.message || 'Failed to start chat. Please try again.');
    }
  };

  const renderVerificationBadges = () => {
    const badges = profile.stats.verifiedBadges || {};
    return (
      <div className="verification-badges">
        {badges.email && (
          <span className="badge badge-verified" title="Email Verified">
            ✓ Email
          </span>
        )}
        {badges.phone && (
          <span className="badge badge-verified" title="Phone Verified">
            ✓ Phone
          </span>
        )}
        {badges.gst && (
          <span className="badge badge-verified" title="GST Verified">
            ✓ GST
          </span>
        )}
        {badges.pan && (
          <span className="badge badge-verified" title="PAN Verified">
            ✓ PAN
          </span>
        )}
        {badges.drivingLicense && (
          <span className="badge badge-verified" title="Driving License Verified">
            ✓ DL
          </span>
        )}
      </div>
    );
  };

  const renderStats = () => {
    const { stats, userType } = profile;

    if (userType === 'seller') {
      return (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-value">{stats.totalProducts || 0}</div>
            <div className="stat-label">Products</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🛍️</div>
            <div className="stat-value">{stats.totalOrders || 0}</div>
            <div className="stat-label">Orders</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-value">{stats.averageRating?.toFixed(1) || '0.0'}</div>
            <div className="stat-label">Rating</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💬</div>
            <div className="stat-value">{stats.totalReviews || 0}</div>
            <div className="stat-label">Reviews</div>
          </div>
        </div>
      );
    } else if (userType === 'buyer' || userType === 'consumer') {
      return (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🛍️</div>
            <div className="stat-value">{stats.totalOrders || 0}</div>
            <div className="stat-label">Orders</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{stats.completedOrders || 0}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💬</div>
            <div className="stat-value">{stats.totalReviews || 0}</div>
            <div className="stat-label">Reviews</div>
          </div>
          {stats.loyaltyTier && (
            <div className="stat-card">
              <div className="stat-icon">🏆</div>
              <div className="stat-value">{stats.loyaltyTier.toUpperCase()}</div>
              <div className="stat-label">Tier</div>
            </div>
          )}
        </div>
      );
    } else if (userType === 'delivery_partner') {
      return (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-value">{stats.totalDeliveries || 0}</div>
            <div className="stat-label">Deliveries</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{stats.successRate || 0}%</div>
            <div className="stat-label">Success Rate</div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderAdditionalInfo = () => {
    if (!isLoggedIn || !profile.additionalInfo) return null;

    const info = profile.additionalInfo;

    return (
      <div className="additional-info">
        <h3>📊 Additional Information (Logged-in users only)</h3>
        <div className="info-grid">
          {info.responseTime && (
            <div className="info-item">
              <span className="info-label">Response Time:</span>
              <span className="info-value">{info.responseTime}</span>
            </div>
          )}
          {info.shippingSpeed && (
            <div className="info-item">
              <span className="info-label">Shipping Speed:</span>
              <span className="info-value">{info.shippingSpeed}</span>
            </div>
          )}
          {info.gstNumber && (
            <div className="info-item">
              <span className="info-label">GST Number:</span>
              <span className="info-value">{info.gstNumber}</span>
            </div>
          )}
          {info.joinDate && (
            <div className="info-item">
              <span className="info-label">Member Since:</span>
              <span className="info-value">{new Date(info.joinDate).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProducts = () => {
    if (!profile.products || profile.products.length === 0) return null;

    return (
      <div className="products-section">
        <h3>🛍️ Featured Products</h3>
        <div className="products-grid">
          {profile.products.map((product) => (
            <div key={product._id} className="product-card">
              <div className="product-image">
                {product.images && product.images[0] ? (
                  <img src={product.images[0].url} alt={product.title} />
                ) : (
                  <div className="no-image">📦</div>
                )}
              </div>
              <div className="product-info">
                <h4>{product.title}</h4>
                <p className="product-price">₹{product.pricing?.basePrice || 0}</p>
                <div className="product-rating">
                  <span>⭐ {product.ratings?.average?.toFixed(1) || '0.0'}</span>
                  <span>({product.ratings?.count || 0} reviews)</span>
                </div>
                {isLoggedIn && (
                  <Link to={`/products/${product._id}`} className="view-product-btn">
                    View Product
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReviews = () => {
    if (!profile.reviews || profile.reviews.length === 0) return null;

    return (
      <div className="reviews-section">
        <h3>⭐ Recent Reviews</h3>
        <div className="reviews-list">
          {profile.reviews.map((review, index) => (
            <div key={review._id || index} className="review-card">
              <div className="review-header">
                <div className="review-user">
                  <div className="review-avatar">
                    {review.user?.businessName?.[0] || review.user?.consumerProfile?.firstName?.[0] || 'U'}
                  </div>
                  <div className="review-user-info">
                    <strong>
                      {review.user?.businessName || 
                       `${review.user?.consumerProfile?.firstName || ''} ${review.user?.consumerProfile?.lastName || ''}`.trim() ||
                       'Anonymous'}
                    </strong>
                    <div className="review-rating">
                      {'⭐'.repeat(review.rating)}
                    </div>
                  </div>
                </div>
                <span className="review-date">
                  {new Date(review.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="review-comment">{review.comment}</p>
              {review.product && (
                <div className="review-product">
                  <small>Product: {review.product.title}</small>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="public-profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-profile-container">
        <div className="error-message">
          <h2>⚠️ {error}</h2>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="public-profile-container">
        <div className="error-message">
          <h2>Profile not found</h2>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container">
      <div className="profile-header">
        <div className="profile-banner">
          <div className="banner-overlay"></div>
        </div>
        
        <div className="profile-header-content">
          <div className="profile-avatar">
            {profile.profileImage ? (
              <img src={profile.profileImage} alt={profile.businessName} />
            ) : (
              <div className="avatar-placeholder">
                {profile.businessName?.[0] || profile.consumerProfile?.firstName?.[0] || 'U'}
              </div>
            )}
          </div>

          <div className="profile-header-info">
            <h1>
              {profile.businessName || 
               `${profile.consumerProfile?.firstName || ''} ${profile.consumerProfile?.lastName || ''}`.trim() ||
               'User'}
            </h1>
            {profile.businessName && profile.consumerProfile && 
             (profile.consumerProfile.firstName || profile.consumerProfile.lastName) && (
              <p className="contact-person">
                Contact: {profile.consumerProfile.firstName} {profile.consumerProfile.lastName}
              </p>
            )}
            <p className="profile-type">
              {profile.userType.charAt(0).toUpperCase() + profile.userType.slice(1).replace('_', ' ')}
            </p>
            {renderVerificationBadges()}
            <div className="profile-meta">
              <span>📅 Member since {new Date(profile.stats.memberSince).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="profile-actions">
            {isOwner && (
              <Link to="/privacy-settings" className="btn-secondary">
                Edit Privacy Settings
              </Link>
            )}
            <button onClick={copyProfileLink} className="btn-icon" title="Copy Link">
              🔗
            </button>
            <button onClick={shareOnWhatsApp} className="btn-icon" title="Share on WhatsApp">
              📱
            </button>
            {!isOwner && (
              <button onClick={handleStartChat} className="btn-primary btn-chat-profile">
                <FaComments /> {isLoggedIn ? 'Message' : 'Message (Login Required)'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="profile-body">
        <div className="profile-tabs">
          <button
            className={`tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          {profile.products && profile.products.length > 0 && (
            <button
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              Products ({profile.products.length})
            </button>
          )}
          {profile.reviews && profile.reviews.length > 0 && (
            <button
              className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews ({profile.reviews.length})
            </button>
          )}
        </div>

        <div className="profile-content">
          {activeTab === 'about' && (
            <div className="tab-content">
              <div className="stats-section">
                <h3>📊 Statistics</h3>
                {renderStats()}
              </div>

              {renderAdditionalInfo()}

              <div className="contact-section">
                <h3>📞 Contact Information</h3>
                <div className="contact-info">
                  {profile.email && (
                    <div className="contact-item">
                      <span className="contact-icon">📧</span>
                      <span>{profile.email}</span>
                    </div>
                  )}
                  {profile.phone && (
                    <div className="contact-item">
                      <span className="contact-icon">📱</span>
                      <span>{profile.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {!isLoggedIn && (
                <div className="login-prompt">
                  <p>🔒 Log in to see more details and connect with this user</p>
                  <Link to="/login" className="btn-primary">Login</Link>
                </div>
              )}
            </div>
          )}

          {activeTab === 'products' && (
            <div className="tab-content">
              {renderProducts()}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="tab-content">
              {renderReviews()}
            </div>
          )}
        </div>
      </div>

      <div className="profile-footer">
        <div className="trust-indicators">
          <div className="trust-item">
            <span className="trust-icon">🛡️</span>
            <span>Verified Profile</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">🔒</span>
            <span>Secure Transactions</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">⚡</span>
            <span>Fast Response</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
